import React, { useState, useEffect, useMemo } from "react";
import {Checkbox, Input} from "antd";
import { notification } from "antd";
import { Spin, Select } from "antd";
import { LoadingOutlined } from "@ant-design/icons";
import { generateYearOptions } from "@/composable/useDates";

import api from '@/api/index'

import VideoCategories from "@/components/categories/VideoCategories";
import BlockGetInfoById from '@/components/video-control/BlockGetInfoById';

import "./styles/serial-main-info.scss";
import {getResponseErrorMessage} from "@/utils/api-service";

const { TextArea } = Input;

interface Props {
  update: (serial:any) => void,
  id: string | number
}


function extractUrlFromHtml(value: string): string {
  const trimmed = value.trim();

  if (trimmed.startsWith('<')) {
    const match = trimmed.match(/src=["']([^"']+)["']/i);
    if (match && match[1]) {
      return convertYoutubeUrl(match[1]);
    }
    return trimmed;
  }

  return convertYoutubeUrl(trimmed);
}

// Конвертирует youtu.be/ID и youtube.com/watch?v=ID в youtube.com/embed/ID
function convertYoutubeUrl(url: string): string {
  // Формат: https://youtu.be/VIDEO_ID?si=...
  const shortMatch = url.match(/youtu\.be\/([^?&]+)/);
  if (shortMatch) {
    const videoId = shortMatch[1];
    return `https://www.youtube.com/embed/${videoId}`;
  }

  // Формат: https://www.youtube.com/watch?v=VIDEO_ID&...
  const watchMatch = url.match(/youtube\.com\/watch\?v=([^&]+)/);
  if (watchMatch) {
    const videoId = watchMatch[1];
    return `https://www.youtube.com/embed/${videoId}`;
  }

  // Формат: https://www.youtube.com/embed/VIDEO_ID?si=...
  const embedMatch = url.match(/youtube\.com\/embed\/([^?&]+)/);
  if (embedMatch) {
    const videoId = embedMatch[1];
    return `https://www.youtube.com/embed/${videoId}`;
  }

  return url;
}


function SerialMainInfo({update, id}: Props) {
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [kinopoiskId, setKinopoiskId] = useState<string>("");
  const [IMDB, setIMDB] = useState<string>("");
  const [year, setYear] = useState<string>("");
  const [name, setName] = useState<string>("");
  const [name_original, setNameOriginal] = useState<string>('')
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true)
  const [isShowCategories, setIsShowCategories] = useState(true)
  const [serealName, setSerealName] = useState('')
  const [isActive, setIsActive] = useState(true);
  const [trailer_url, setTrailerUrl] = useState<string>('') // новое поле

  const fetchSereal = async() => {
    try {
      const response = await api.serie.get(id)
      await api.categories.getCategoriesContentManager();
      const respSerie = response.data.data

      setSerealName(respSerie.title)
      setTitle(respSerie.title)
      setDescription(respSerie.description)
      setKinopoiskId(respSerie.kinopoisk_id)
      setIMDB(respSerie.imdb_id)
      setYear(respSerie.release_year)
      setName(respSerie.title)
      setNameOriginal(respSerie.title_original)
      setTrailerUrl(respSerie.trailer_url || '') // инициализация при редактировании

      setIsActive(respSerie.status_id === 1 ? true : false)

      const formattedCategories = respSerie.categories.map((category) => category.id)
      setCategories(formattedCategories)

    } catch(err) {
      console.error(err);
      const messageFormatter = getResponseErrorMessage(err, 'Ошибка при получении сериала')
      notification.error({
        message: messageFormatter,
      });

    } finally {
      setIsLoading(false)
    }
  }

  const handleChangeCategories = (category: string) => {
    const list = categories;
    const index = list.indexOf(category);

    if (index > -1) {
      list.splice(index, 1);
    } else {
      list.push(category);
    }

    setCategories(list);
  };

  const updateImportOptions = (key:string, value:any) => {
    if(key === 'imdb_id') {
      setIMDB(value)
    } else {
      setKinopoiskId(value)
    }
  }

  const updateAllFromImport = (data) => {
    setName(data.name || '')
    setDescription(data.description || '')
    setYear(data.release_year || '')
    setCategories(data.categories || '')
    setNameOriginal(data.name_original)
    setIsActive(false)

    updateCategoriesView()
  }

  const updateCategoriesView = () => {
    setIsShowCategories(false)

    setTimeout(() => {
      setIsShowCategories(true)
    }, 0)
  }

  // Формируем ссылку на YouTube по мере ввода названия
  const youtubeSearchUrl = useMemo(() => {
    const searchTitle = name || name_original || '';
    if (!searchTitle.trim()) {
      return 'https://www.youtube.com/results?search_query=трейлер';
    }
    const query = encodeURIComponent(`трейлер ${searchTitle}`);
    return `https://www.youtube.com/results?search_query=${query}`;
  }, [name, name_original]);

  const handleTrailerUrlChange = (value: string) => {
    const extracted = extractUrlFromHtml(value);
    setTrailerUrl(extracted);
  }

  useEffect(() => {
    update({
      name: name,
      description: description,
      categories: categories,
      release_year: year,
      kinopoiskId: kinopoiskId,
      IMDB: IMDB,
      name_original: name_original,
      serealNameTitle: serealName,
      isActive: isActive,
      trailer_url: trailer_url // передаём в родитель
    })

  }, [name, description, categories, year, kinopoiskId, IMDB, name_original, isActive, trailer_url])

  useEffect(() => {
    if(Number(id)) {
      fetchSereal()

    } else {
      setIsLoading(false)
    }

  }, [id])

  if(isLoading) {
    return (
        <div className="app-loading-block">
          <Spin size="large" indicator={<LoadingOutlined />} />
        </div>
    )
  }

  return (
      <div className="serial-main-info">
        <div className="form-group">
          <label className="form-group__label">
            Оригинальное название
          </label>
          <Input
              value={name_original}
              onChange={(e) => {
                setNameOriginal(e.target.value);
              }}
              placeholder="Название"
          />
        </div>

        <div className="form-group">
          <label className="form-group__label">
            Название сериала <span className="app-required">*</span>
          </label>
          <Input
              value={name}
              onChange={(e) => {
                setName(e.target.value)
              }}
              placeholder="Введите название сериала"
          />
        </div>

        <div className="form-group">
          <label className="form-group__label">
            Описание
          </label>
          <TextArea
              className={"app-textarea"}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={4}
              placeholder="Описание"
          />
        </div>

        {/* Новое поле: ссылка на трейлер */}
        <div className="form-group">
          <label className="form-group__label">
            Ссылка на трейлер
            &#160;
            (<a
                href={youtubeSearchUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="serial-main-info__youtube-link"
                title="Найти трейлер на YouTube"
            >
              Найти на YouTube
            </a>)
          </label>
          <Input
              value={trailer_url}
              onChange={(e) => handleTrailerUrlChange(e.target.value)}
              placeholder="https://... или вставьте iframe-код"
          />
        </div>

        <div className="form-group form-group__year">
          <div className="form-group">
            <label className="form-group__label" htmlFor={"Year"}>
              Год выпуска <span className="app-required">*</span>
            </label>
            <Select
                id={"Year"}
                className={"app-select"}
                value={year}
                onChange={(val) => {
                  setYear(val);
                }}
                placeholder="Выберите год"
                options={generateYearOptions()}
                allowClear
            />
          </div>
        </div>

        <div className="form-group form-group--row">
          <Checkbox
              id="Active"
              className="app-unchecked-checkbox"
              checked={isActive}
              onChange={(event) => {
                setIsActive(event.target.checked)
              }}
          />
          <label className={"form-group-label"} htmlFor="Active">
            активный
          </label>
        </div>

        <BlockGetInfoById type="serial" name_original={name_original} updateOption={updateImportOptions}  update={updateAllFromImport} defaultData={{kinopoiskId: kinopoiskId, IMDB: IMDB}} isEdit={!!Number(id)}  />
        {
            isShowCategories && <VideoCategories onChange={handleChangeCategories} list={categories} />
        }
      </div>
  );
}

export default SerialMainInfo;